import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { streamImage, type ImageStreamEvent, type ImageStyle } from './image-api'
import { vaultKeys } from '@/features/vault/use-vault'
import {
  abortRun,
  beginRun,
  clearRun,
  isRunController,
  setRunAbortController,
  updateRunState,
  useRunStore,
} from '@/features/runs/run-store'

const AUTO_COLLAPSE_MS = 5_000

export interface ImageState {
  status: string
  detail?: string
  finalPrompt?: string
  model?: string
  result?: { vault_id: string; vault_path: string; storage_url?: string }
  error?: string
  isRunning: boolean
}

export function useImage() {
  const qc = useQueryClient()
  // Globaler Store statt lokalem State (M2-B Run-Survival).
  const slice = useRunStore((s) => s.image)
  const state = slice.state

  const start = useCallback(
    async (opts: {
      prompt: string
      style?: ImageStyle
      brand?: string
      primaryBrandId?: string | null
      sessionId?: string
    }) => {
      abortRun('image')
      const controller = new AbortController()
      setRunAbortController('image', controller)

      beginRun('image', { status: 'queued', isRunning: true }, opts.sessionId ?? null)

      try {
        await streamImage({
          ...opts,
          signal: controller.signal,
          onEvent: (event: ImageStreamEvent) => {
            if (!isRunController('image', controller)) return
            updateRunState('image', (prev) => {
              switch (event.type) {
                case 'status':
                  return { ...prev, status: event.phase, detail: event.detail }
                case 'done':
                  return {
                    ...prev,
                    status: 'done',
                    isRunning: false,
                    finalPrompt: event.final_prompt,
                    model: event.model,
                    result: {
                      vault_id: event.vault_id,
                      vault_path: event.vault_path,
                      storage_url: event.storage_url,
                    },
                  }
                case 'error':
                  return { ...prev, status: 'error', isRunning: false, error: event.message }
                default:
                  return prev
              }
            })
            if (event.type === 'done') {
              void qc.invalidateQueries({ queryKey: vaultKeys.files() })
              toast.success(`Bild erstellt: ${event.vault_path}`)
            }
            if (event.type === 'error') toast.error(event.message)
          },
        })
      } catch (err) {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Bild-Generierung fehlgeschlagen'
        toast.error(message)
        updateRunState('image', (prev) => ({ ...prev, status: 'error', isRunning: false, error: message }))
      }
    },
    [qc],
  )

  const reset = useCallback(() => {
    abortRun('image')
    clearRun('image')
  }, [])

  // Auto-Collapse 5s nach 'done' — Banner ist visuell nicht mehr informativ.
  // Laeuft nur, solange eine Komponente den Hook nutzt (gemountet ist); nach
  // einem Ruecksprung startet der Timer neu, der finale Stand bleibt bis dahin.
  useEffect(() => {
    if (state.status !== 'done') return
    const timer = window.setTimeout(() => clearRun('image'), AUTO_COLLAPSE_MS)
    return () => window.clearTimeout(timer)
  }, [state.status])

  return { start, reset, state, sessionId: slice.sessionId }
}
